-- | This implements the API functionality for accessing and saving documents.
-- | A document is a filled or work in progress form saved to the backend. On
-- | the backend, these are just called "forms" (distinct from "formtypes"
-- | which define the layout), but here we call them documents to distinguish.
module API.Document where

import Common

import API.Attachments (Attachment)
import API.Comments (Comment, CommentResponse, processComment)
import API.Expectations (ExpCourse)
import API.FormBase (FormBase, Mutations, FormID)
import API.Search (SearchSemester, SearchTeacher)
import API.SelEmployer (PosConEmployer, SelContact, SelPosition)
import API.SelStudent (SelStudent)
import API.Signature (SigVerification, SignResult, SignatureRefreshBase)
import API.Types (DocumentID, StudentID, TeacherID, SemesterID)
import Data.Argonaut (Json)
import GraphQL.Query (Mutation, Query, buildMutation, buildQuery, nameQuery)
import GraphQL.Type (DyNamedType(..), NamedType, namedType)
import Internal.Luxon (LDateTime)
import Record as Rec
import Type.Prelude (Proxy(..))
import Widgets.Form.Access (FormAccess)
import Widgets.Signature.Types (DocSigField)


-- | Document with some data saved to the backend.
type Document = DocumentBase
    ( formId :: FormID
    , formName :: String
    , mutations :: Mutations
    , comments :: Array Comment
    , signature_fields :: Array DocSigField
    , json :: Json
    )

type DocumentResponse = DocumentBase
    ( comments :: Array CommentResponse
    , signature_fields :: Array DocSigField
    , type :: String
    , data :: Json
    )

type DocumentBase r =
    { id :: DocumentID
    , student :: SelStudent
    , semester :: SearchSemester
    , teacher :: Maybe SearchTeacher
    -- ^ Mutation queries for creating, updating, and deleting this document.
    , signatures :: Array SignResult
    , verifications :: Array SigVerification
    , updated_at :: LDateTime
    , created_at :: LDateTime
    , locked_at :: Maybe LDateTime
    , exports :: Array FormReport
    , access :: FormAccess
    , employer :: Maybe PosConEmployer
    , position :: Maybe SelPosition
    , contact :: Maybe SelContact
    , courses :: Array ExpCourse
    , attachments :: Array Attachment
    , can :: DocPolicy
    | r
    }

type DocPolicy =
    { comment :: Boolean
    , private_comment :: Boolean
    , attach :: Boolean
    , lock :: Boolean
    -- FIXME: The below permissions are unused
    , update :: Boolean
    , delete :: Boolean
    , copy :: Boolean
    , create_employers :: Boolean
    }

-- Processs a `DocumentResponse` into a final `Document` object
processDocument :: FormID -> Mutations -> DocumentResponse -> Document
processDocument formId mutations = Rec.merge { formId, mutations }
    <<< Rec.rename (Proxy :: Proxy "type") (Proxy :: Proxy "formName")
    <<< Rec.rename (Proxy :: Proxy "data") (Proxy :: Proxy "json")
    <<< Rec.modify (Proxy :: Proxy "comments") (map processComment)


-- | Gets a document from the backend based on its ID.
fetchDocumentQuery :: forall r. FormBase r -> DocumentID -> Query Document
fetchDocumentQuery ft id = nameQuery "GetDocument" $
    processDocument ft.id ft.mutations <$> buildQuery "form" { id }

type CreateDocArgs =
    { student_id :: StudentID
    , teacher_id :: Maybe TeacherID
    , semester :: SemesterID
    }

-- | Creates a new document on the backend of the given formtype.
createDocument :: forall r. FormBase r -> CreateDocArgs -> Mutation Document
createDocument ft args = nameQuery "CreateDocument" $
    processDocument ft.id ft.mutations <$> buildMutation ft.mutations.create args

type UpdateDocArgs =
    { semester :: SemesterID
    , teacher :: TeacherID
    }

-- | Updates an existing document with new JSON data.
updateDocument :: Document -> Json -> UpdateDocArgs -> Mutation Document
updateDocument doc json { semester, teacher } = nameQuery "UpdateDocument" $
    processDocument doc.formId doc.mutations <$> buildMutation doc.mutations.update
        { id: doc.id
        , semester
        , teacher_id: teacher
        , data: DyNamedType (doc.formName <> "Input") json
        }


type CopyDocRequest = { form_id :: DocumentID, student_id :: StudentID }

copyDocuments :: Array (CopyDocRequest) -> Mutation (Array DocumentID)
copyDocuments copies = nameQuery "CopyDocuments" $ process <$> buildMutation "copyForms"
    { input: map namedType copies :: Array (NamedType "CopyFormInput" _) }
  where
    process :: { forms :: Array { id :: DocumentID } } -> Array DocumentID
    process r = map _.id r.forms

deleteDocuments :: Array (DocumentID) -> Mutation Unit
deleteDocuments ids = nameQuery "DeleteDocuments" $ buildMutation "deleteForms" { form_id: ids }


type FormReport =
    { url :: String
    , label :: String
    , format :: String
    }

processExports :: { exports :: Array FormReport } -> Array FormReport
processExports = _.exports

exportDocuments :: Array (DocumentID) -> Query (Array FormReport)
exportDocuments ids = nameQuery "ExportDocuments" $ processExports <$> buildQuery "forms" { ids: ids }

type CreateFormRequest =
    { type_id :: FormID
    , student_id :: StudentID
    , teacher_id :: Maybe TeacherID
    , semester :: Maybe SemesterID
    }

createAndExport :: Array CreateFormRequest -> Mutation (Array FormReport)
createAndExport forms = nameQuery "CreateAndExportDocuments" $ processExports <$> buildMutation "createForms"
    { input: map namedType forms :: Array (NamedType "CreateFormInput" _) }

-- | Input type for toggling document lock status
type ToggleLockInput =
    { form_id :: DocumentID
    , locked :: Boolean
    }

-- | Response type for toggle lock mutation, includes fields needed by Step3
type ToggleLockResult = SignatureRefreshBase
    (id :: DocumentID
    )

applyLockPermission
    :: forall canR recordR
     . ToggleLockResult
    -> { locked_at :: Maybe LDateTime, can :: { lock :: Boolean, update :: Boolean | canR } | recordR }
    -> { locked_at :: Maybe LDateTime, can :: { lock :: Boolean, update :: Boolean | canR } | recordR }
applyLockPermission { locked_at, can } record =
    record
        { locked_at = locked_at
        , can = record.can { lock = can.lock, update = can.update }
        }

-- | Bulk mutation to toggle lock status on multiple documents
-- | Uses updateForms mutation with locked field
toggleDocumentLocks :: Array ToggleLockInput -> Mutation (Array ToggleLockResult)
toggleDocumentLocks inputs = nameQuery "ToggleDocumentLocks" $
    process <$> buildMutation "updateForms"
        { input: map namedType inputs :: Array (NamedType "UpdateFormInput" _) }
  where
    process :: { forms :: Array ToggleLockResult } -> Array ToggleLockResult
    process = _.forms
